import type * as THREE from 'three';
import { createColor } from './potreeThree';
import { Color } from '../types/graphqlTypes';

/**
 * Convert hex string to Color object for ColorPopover
 * @param hex - Hex color string (with or without #)
 * @returns Color object with r, g, b values in 0-255 range and a=1
 */
export const hexToColor = (hex: string): Color => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: 1,
      }
    : { r: 0, g: 0, b: 0, a: 1 };
};

/**
 * Convert Color object to hex string
 * @param color - Color object with r, g, b values in 0-255 range
 * @returns Hex color string with # prefix
 */
export const colorToHex = (color: Color): string => {
  // Safety check for undefined color values
  if (!color || typeof color.r !== 'number' || typeof color.g !== 'number' || typeof color.b !== 'number') {
    console.warn('Invalid color object:', color);
    return '#000000';
  }

  const toHex = (c: number) => {
    // Ensure c is a valid number and clamp to 0-255 range
    const clamped = Math.max(0, Math.min(255, Math.floor(c || 0)));
    return clamped.toString(16).padStart(2, '0');
  };
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
};

/**
 * Type guard to check if a value is a Color object
 * @param val - Value to check
 * @returns True if val is a Color object
 */
export const isColor = (val: unknown): val is Color => {
  return val != null && typeof val === 'object' && !Array.isArray(val) && 'r' in val && typeof (val as Record<string, unknown>).r === 'number';
};

/**
 * Convert THREE.Color (0-1 range) to Color object (0-255 range)
 * @param threeColor - THREE.Color object with r, g, b values in 0-1 range
 * @returns Color object with r, g, b values in 0-255 range and a=1
 */
export const threeColorToColor = (threeColor: THREE.Color): Color => {
  return {
    r: Math.round(threeColor.r * 255),
    g: Math.round(threeColor.g * 255),
    b: Math.round(threeColor.b * 255),
    a: 1,
  };
};

/**
 * Convert Color object (0-255 range) to THREE.Color format (0-1 range)
 * @param color - Color object with r, g, b values in 0-255 range
 * @returns Object with r, g, b values in 0-1 range for THREE.Color
 */
export const colorToThreeColor = (color: Color): THREE.Color => {
  // Convert to hex and use standard THREE.Color constructor to ensure consistent color space conversion
  const hex = (color.r << 16) | (color.g << 8) | color.b;
  return createColor(hex);
};
